/**
 * Axios Client Configuration
 * Centralized HTTP client with request/response interceptors
 * 
 * Features:
 * - Automatic Session-ID header injection
 * - Request/Response logging
 * - Error handling
 * - Request ID tracking for debugging
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getSessionId } from '../security/authStorage';
import { performSignOut } from '../security/signOut';
import { createLogger } from '../utils/logging';

const logger = createLogger('API Client');

// ============================================================================
// Configuration
// ============================================================================

const isDevelopment = process.env.NODE_ENV === 'development';

// Feature flags for logging
// Check window.environments (loaded from environments.json) or fall back to dev mode
function shouldLogRequests(): boolean {
  const environments = (window as any)['environments'];
  if (environments && typeof environments.LOG_REQUESTS === 'boolean') {
    return environments.LOG_REQUESTS;
  }
  // Fallback to dev mode if environments not loaded yet
  return isDevelopment;
}

// ============================================================================
// Axios Instance
// ============================================================================

export const axiosClient: AxiosInstance = axios.create({
  baseURL: '/',
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Auth Paths
// ============================================================================

// Auth paths that must never trigger a sign-out loop (a 401 here is either expected
// or would recurse into performSignOut, which itself calls the logout endpoint)
const AUTH_PATHS = ['/loginap', 'secure/refresh', 'secure/logout', 'secure/validate-session'];

// ============================================================================
// Request ID Generator (for tracing)
// ============================================================================

let requestIdCounter = 0;

function generateRequestId(): string {
  requestIdCounter = (requestIdCounter + 1) % 10000;
  return `#${requestIdCounter}`;
}

// ============================================================================
// Request Interceptor
// ============================================================================

axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const requestId = generateRequestId();
    
    // Add request ID for tracing
    config.headers.set('X-Request-ID', requestId);
    
    // Auto-inject Session-ID header if session exists
    const sessionId = getSessionId();
    if (sessionId) {
      config.headers.set('session-id', sessionId);
    }
    
    // Log request (if enabled)
    if (shouldLogRequests()) {
      logger.debug(`Request ${requestId}`, `${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }
    
    return config;
  },
  (error: AxiosError) => {
    logger.error('Request Error', error.message, {
      url: error.config?.url,
      method: error.config?.method,
    });
    return Promise.reject(error);
  }
);

// ============================================================================
// Response Interceptor
// ============================================================================

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const requestId = response.config.headers?.['X-Request-ID'];
    
    // Log response (if enabled)
    if (shouldLogRequests()) {
      logger.debug(`Response ${requestId}`, `${response.status} ${response.statusText}`, {
        url: response.config.url,
      });
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const requestId = error.config?.headers?.['X-Request-ID'];
    const status = error.response?.status;
    const url = error.config?.url ?? '';

    // Always log errors (even in production)
    logger.error(`Failed ${requestId}`, `${error.config?.method?.toUpperCase()} ${url}`, {
      status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data,
    });

    // 401 means the session is already invalid — the sliding-refresh heartbeat (see
    // IdleTimerContainer) is responsible for keeping the session alive *before* it expires.
    // By the time a 401 comes back, secure/refresh would also 401 (it requires a still-valid
    // session), so there is nothing to gain by attempting a refresh-and-retry here. Sign out
    // directly instead. Skip auth paths (would loop) and pre-login requests with no session
    // yet (would cause a spurious sign-out).
    const isAuthPath = AUTH_PATHS.some((path) => url.includes(path));
    const hasSession = !!getSessionId();
    if (status === 401 && hasSession && !isAuthPath) {
      logger.warn('Session Invalid', 'Received 401, signing out', { url });
      await performSignOut();
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if running in no-auth mode
 */
export function isNoAuthMode(): boolean {
  const environments = (window as any)['environments'];
  return environments?.no_auth === true;
}

/**
 * Get environment-specific API base URL
 */
export function getApiBaseUrl(service: string): string {
  const env = process.env.REACT_APP_API_ENV;
  
  // Production/Staging environments may have different base paths
  if (env === 'stg' || env === 'prd') {
    return `./user-alias-service/${service}`;
  }
  
  return `/${service}`;
}

export default axiosClient;
